import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Navigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FaBell, FaUsers, FaCalendarAlt, FaComments,
  FaQuestionCircle, FaBullhorn, FaChartLine,
  FaUserFriends, FaImages, FaHandsHelping,
  FaThumbsUp, FaReply, FaArrowRight, FaCog,
  FaTh, FaFileAlt, FaCheckCircle, FaClock, FaDownload
} from "react-icons/fa";
import { announcementAPI, notificationAPI, sessionAPI, applicationAPI } from "../../services/api";

// Import Role-Based Dashboards
import ApplicantDashboard from "./ApplicantDashboard";
import StudentDashboard from "./StudentDashboard";
import StudentManagerDashboard from "./StudentManagerDashboard";
import EmployerDashboard from "./EmployerDashboard";
import AdminDashboard from "./AdminDashboard";
import AlumniDashboard from "./AlumniDashboard";

// Import Shared Dashboard Components
import {
  DashboardHeader,
  DashboardTabs,
  StatCard,
  LoadingSpinner,
  EmptyState
} from '../../components/dashboard';

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();

  // State
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    posts: 0,
    connections: 0,
    events: 0,
    contributions: 0
  });
  const [announcements, setAnnouncements] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [notifications, setNotifications] = useState(0);
  const [applications, setApplications] = useState([]);
  const [applicationFilters, setApplicationFilters] = useState({
    status: 'all',
    village: 'all'
  });

  // Refs
  const dataLoadedRef = useRef(false);
  const isMounted = useRef(true);

  // Constants
  const tabs = useMemo(() => [
    { id: 'overview', label: 'Overview', icon: FaTh },
    { id: 'announcements', label: 'Announcements', icon: FaBullhorn },
    { id: 'events', label: 'Events', icon: FaCalendarAlt },
    { id: 'community', label: 'Community', icon: FaUsers },
    { id: 'applications', label: 'My Applications', icon: FaFileAlt }
  ], []);

  const quickActions = [
    { icon: <FaBullhorn />, label: "Create Post", link: "/community/create", color: "from-blue-500 to-blue-600" },
    { icon: <FaQuestionCircle />, label: "Ask Question", link: "/questions/ask", color: "from-green-500 to-green-600" },
    { icon: <FaUserFriends />, label: "Find Members", link: "/members", color: "from-purple-500 to-purple-600" },
    { icon: <FaImages />, label: "View Gallery", link: "/gallery", color: "from-pink-500 to-pink-600" },
    { icon: <FaHandsHelping />, label: "Volunteer", link: "/volunteer", color: "from-orange-500 to-orange-600" },
    { icon: <FaCog />, label: "Settings", link: "/profile/edit", color: "from-gray-500 to-gray-600" }
  ];

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Auth & Role Logic
  useEffect(() => {
    if (!authLoading && isMounted.current) {
      if (user) {
        const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
        if (storedUser && storedUser.role !== user.role) {
          console.warn('⚠️ Dashboard: Role mismatch! State:', user.role, 'localStorage:', storedUser.role);
        }

        // Specific fix for a known user issue
        if (user.email === 'shubhamsinghrajput60877@gmail.com' && user.role && user.role !== 'applicant') {
          const refreshUser = async () => {
            try {
              const { authAPI } = await import('../../services/api');
              const data = await authAPI.getCurrentUser();
              if (data && data.user && data.user.role === 'applicant') {
                localStorage.setItem('user', JSON.stringify(data.user));
                window.location.reload();
              }
            } catch (err) {
              console.error('❌ Dashboard: Failed to refresh user:', err);
            }
          };
          refreshUser();
        }
      }

      // Small delay to ensure smooth transition
      setTimeout(() => {
        if (isMounted.current) setLoading(false);
      }, 100);
    }
  }, [authLoading, user]);

  // Data Loading Logic
  const loadDashboardData = useCallback(async () => {
    if (dataLoadedRef.current) return;
    dataLoadedRef.current = true;

    try {
      const [annRes, notifRes, sessionsRes] = await Promise.all([
        announcementAPI.getMyFeed().catch(() => ({ announcements: [] })),
        notificationAPI.getUnreadCount().catch(() => ({ count: 0 })),
        sessionAPI.getActive().catch(() => ({ sessions: [] }))
      ]);

      if (!isMounted.current) return;

      const newAnnouncements = annRes.announcements?.slice(0, 5) || [];
      const newCount = notifRes.count || 0;
      const newEvents = (sessionsRes.sessions || []).slice(0, 3).map(session => ({
        id: session.id,
        title: session.name,
        date: session.start_date ? new Date(session.start_date).toLocaleDateString() : 'TBD',
        time: session.start_date ? new Date(session.start_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
        location: session.location || 'TBD',
        rsvpStatus: 'not-responded'
      }));

      setAnnouncements(newAnnouncements);
      setNotifications(newCount);
      setUpcomingEvents(newEvents);

      setStats(prev => ({
        ...prev,
        events: sessionsRes.sessions?.length || 0
      }));

      // Load ALL scholarship applications across sessions for committee members (member role)
      try {
        const allSessionsRes = await sessionAPI.getAll().catch(() => ({ sessions: [] }));
        const allSessions = allSessionsRes.sessions || [];
        let allApplications = [];

        for (const session of allSessions) {
          try {
            const res = await applicationAPI.getBySession(session.id);
            allApplications = [...allApplications, ...(res.applications || [])];
          } catch (err) {
            console.error(`Failed to load applications for session ${session.id}:`, err);
          }
        }

        setApplications(allApplications);
      } catch (err) {
        console.error('Failed to load applications for member dashboard:', err);
      }

    } catch (err) {
      console.error("Dashboard data load error", err);
    }
  }, []);

  useEffect(() => {
    if (authLoading || !user || user.role !== 'member') return;
    loadDashboardData();
  }, [authLoading, user, loadDashboardData]);

  // Derived data for applications tab (filters)
  const uniqueVillages = useMemo(() =>
    Array.from(new Set(
      (applications || [])
        .map(app => app.address_village)
        .filter(v => v && v.trim() !== '')
    )).sort(),
    [applications]
  );

  const filteredApplications = useMemo(() => {
    if (!applications) return [];

    return applications.filter(app => {
      // Status filter
      if (applicationFilters.status !== 'all') {
        if (applicationFilters.status === 'pending') {
          if (!['submitted', 'under_review'].includes(app.status)) return false;
        } else if (app.status !== applicationFilters.status) {
          return false;
        }
      }

      // Village filter
      if (applicationFilters.village !== 'all') {
        if (app.address_village !== applicationFilters.village) return false;
      }

      return true;
    });
  }, [applications, applicationFilters]);

  const handleDownloadApplication = async (appId) => {
    try {
      const blob = await applicationAPI.downloadPDF(appId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `application_${appId}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download application PDF:', err);
      toast.error('Failed to download application PDF.');
    }
  };

  // Handle Loading State
  if (loading || authLoading) {
    return <LoadingSpinner />;
  }

  // Handle No User
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Handle Role Redirection
  if (user && user.role) {
    const userRole = String(user.role).toLowerCase().trim();
    if (userRole === 'applicant') return <ApplicantDashboard />;
    if (userRole === 'student_manager') return <StudentManagerDashboard />;
    if (userRole === 'employer') return <EmployerDashboard />;
    if (userRole === 'admin') return <AdminDashboard />;
    if (userRole === 'alumni') return <AlumniDashboard />;
    if (userRole === 'student') return <StudentDashboard />;
  }

  // Format Helpers
  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    return date.toLocaleDateString();
  };

  // Render Member Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <DashboardHeader
          icon={FaUserFriends}
          title={`Welcome back, ${user.firstName || "Member"}!`}
          subtitle="Here's what's happening in your community today"
          iconBgColor="bg-primary-50"
          iconColor="text-primary-600"
        />

        <DashboardTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Your Posts"
                value={stats.posts}
                icon={FaBullhorn}
                iconColor="bg-blue-50 text-blue-600"
                onClick={() => { }}
              />
              <StatCard
                title="Connections"
                value={stats.connections}
                icon={FaUsers}
                iconColor="bg-green-50 text-green-600"
                onClick={() => { }}
              />
              <StatCard
                title="Upcoming Events"
                value={stats.events}
                icon={FaCalendarAlt}
                iconColor="bg-purple-50 text-purple-600"
                onClick={() => { }}
              />
              <StatCard
                title="Contributions"
                value={stats.contributions}
                icon={FaHandsHelping}
                iconColor="bg-yellow-50 text-yellow-600"
                onClick={() => { }}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content Area */}
              <div className="lg:col-span-2 space-y-8">

                {/* Announcements */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      <FaBullhorn className="text-primary-600" />
                      Latest Announcements
                    </h2>
                    <span className="text-sm text-gray-500">{announcements.length} new</span>
                  </div>

                  {announcements.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {announcements.map((announcement) => (
                        <div key={announcement.id} className="py-4 first:pt-0 last:pb-0">
                          <div className="flex items-start gap-4">
                            {announcement.is_pinned && (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-bold rounded-full whitespace-nowrap">
                                PINNED
                              </span>
                            )}
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900 hover:text-primary-600 transition-colors">
                                <Link to={`/announcements/${announcement.id}`}>{announcement.title}</Link>
                              </h3>
                              <p className="text-gray-600 mt-1 text-sm line-clamp-2">{announcement.content}</p>
                              <div className="flex items-center justify-between mt-2">
                                <div className="text-xs text-gray-500">
                                  {formatTimeAgo(announcement.created_at)} • {announcement.created_by_name || 'Admin'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="pt-4 mt-2">
                        <Link to="/announcements" className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1">
                          View All Announcements <FaArrowRight size={12} />
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <EmptyState message="No announcements yet." icon={FaBullhorn} />
                  )}
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {quickActions.map((action, index) => (
                      <Link
                        key={index}
                        to={action.link}
                        className="group p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-primary-200 hover:shadow-md transition-all duration-300 text-center flex flex-col items-center"
                      >
                        <div className={`p-3 bg-gradient-to-br ${action.color} rounded-xl text-white mb-3 group-hover:scale-110 transition-transform`}>
                          {action.icon}
                        </div>
                        <h4 className="font-semibold text-gray-900 text-sm group-hover:text-primary-600 transition-colors">
                          {action.label}
                        </h4>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-8">
                {/* Upcoming Events Widget */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FaCalendarAlt className="text-primary-600" />
                    Upcoming Events
                  </h2>

                  {upcomingEvents.length > 0 ? (
                    <div className="space-y-4">
                      {upcomingEvents.map((event) => (
                        <div key={event.id} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <h4 className="font-semibold text-gray-900">{event.title}</h4>
                          <div className="text-sm text-gray-600 mt-2 space-y-1">
                            <div className="flex items-center gap-2"><FaCalendarAlt size={12} /> {event.date} at {event.time}</div>
                            <div className="flex items-center gap-2">📍 {event.location}</div>
                          </div>
                        </div>
                      ))}
                      <Link to="/volunteer" className="block text-center text-sm font-medium text-primary-600 hover:text-primary-700 mt-2">
                        View Calendar
                      </Link>
                    </div>
                  ) : (
                    <EmptyState message="No upcoming events." icon={FaCalendarAlt} />
                  )}
                </div>

                {/* Community Summary */}
                <div className="bg-gradient-to-br from-gray-900 to-primary-900 rounded-2xl shadow-lg p-6 text-white">
                  <h3 className="text-xl font-bold mb-4">Community Pulse</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300 text-sm">Active Members</span>
                      <span className="font-bold">150+</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300 text-sm">Discussions</span>
                      <span className="font-bold">40+</span>
                    </div>
                    <div className="pt-4 border-t border-white/10 text-xs text-gray-400">
                      You're part of a growing community.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'announcements' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 min-h-[400px]">
            <h2 className="text-xl font-bold text-gray-900 mb-6">All Announcements</h2>
            {announcements.length > 0 ? (
              <div className="grid gap-6">
                {announcements.map((announcement) => (
                  <div key={announcement.id} className="p-6 border border-gray-100 rounded-xl hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{announcement.title}</h3>
                        <p className="text-gray-600 mb-4">{announcement.content}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>By {announcement.created_by_name || 'Admin'}</span>
                          <span>•</span>
                          <span>{formatTimeAgo(announcement.created_at)}</span>
                        </div>
                      </div>
                      {announcement.is_pinned && <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-bold">PINNED</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState icon={FaBullhorn} message="No announcements found." />
            )}
          </div>
        )}

        {activeTab === 'applications' && (
          <div className="space-y-8">
            {/* Applications Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Applications"
                value={applications.length}
                icon={FaFileAlt}
                iconColor="bg-blue-50 text-blue-600"
              />
              <StatCard
                title="Submitted Applications"
                value={applications.filter(app => app.status && app.status !== 'draft').length}
                icon={FaCheckCircle}
                iconColor="bg-green-50 text-green-600"
              />
              <StatCard
                title="Draft Applications"
                value={applications.filter(app => app.status === 'draft').length}
                icon={FaClock}
                iconColor="bg-yellow-50 text-yellow-600"
              />
              <StatCard
                title="Selected / Waitlisted"
                value={applications.filter(app => ['selected', 'waitlisted'].includes(app.status)).length}
                icon={FaChartLine}
                iconColor="bg-purple-50 text-purple-600"
              />
            </div>

            {/* Filters and Applications List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200 flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Scholarship Applications</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Read-only view of all scholarship applications (drafts, submitted, selected, and more).
                    </p>
                  </div>
                </div>

                {/* Filters (similar to admin dashboard) */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mt-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Status:</label>
                      <select
                        value={applicationFilters.status}
                        onChange={(e) => setApplicationFilters(prev => ({ ...prev, status: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      >
                        <option value="all">All Status</option>
                        <option value="draft">Draft</option>
                        <option value="submitted">Submitted</option>
                        <option value="pending">Pending Review</option>
                        <option value="under_review">Under Review</option>
                        <option value="selected">Selected</option>
                        <option value="rejected">Rejected</option>
                        <option value="waitlisted">Waitlisted</option>
                      </select>
                    </div>

                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Village:</label>
                      <select
                        value={applicationFilters.village}
                        onChange={(e) => setApplicationFilters(prev => ({ ...prev, village: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm min-w-[150px]"
                      >
                        <option value="all">All Villages</option>
                        {uniqueVillages.map(village => (
                          <option key={village} value={village}>{village}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <button
                    onClick={() => setApplicationFilters({ status: 'all', village: 'all' })}
                    className="self-start px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium"
                  >
                    Reset Filters
                  </button>
                </div>
              </div>

              {applications.length === 0 ? (
                <div className="text-center py-12">
                  <FaFileAlt className="text-5xl text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg font-medium mb-2">
                    No scholarship applications found.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
                  {filteredApplications.map((app) => (
                    <div
                      key={app.id}
                      className="p-6 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-lg font-semibold text-gray-900 truncate">
                              {app.session_name || 'Scholarship Application'}
                            </h3>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Stage {app.stage || 1} of 4
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mt-1">
                            <span>
                              Status:{' '}
                              <span className="font-medium text-gray-900">
                                {app.status ? app.status.replace('_', ' ').toUpperCase() : 'N/A'}
                              </span>
                            </span>
                            <span>
                              Submitted:{' '}
                              {app.submitted_at
                                ? new Date(app.submitted_at).toLocaleDateString()
                                : 'Not submitted'}
                            </span>
                            <span>
                              Last Updated:{' '}
                              {app.updated_at
                                ? new Date(app.updated_at).toLocaleDateString()
                                : 'N/A'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                            View only
                          </span>
                          <Link
                            to={`/applications/${app.id}`}
                            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            View Details
                            <FaArrowRight size={12} />
                          </Link>
                          <button
                            type="button"
                            onClick={() => handleDownloadApplication(app.id)}
                            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <FaDownload size={12} />
                            Download PDF
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Placeholder for other tabs */}
        {(activeTab === 'events' || activeTab === 'community') && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="inline-flex p-4 bg-gray-100 rounded-full text-gray-400 mb-4">
              {activeTab === 'events' ? <FaCalendarAlt size={32} /> : <FaUsers size={32} />}
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Coming Soon</h3>
            <p className="text-gray-600 mb-6">This section is under development. Check back later!</p>
            <button
              onClick={() => setActiveTab('overview')}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              Return to Overview
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
